/**
 * Keyboard focus for modal layers (ux-screens §11, M4-14): when a modal opens, focus moves into it (its
 * `[data-autofocus]`, else its heading, else its first control); Tab and Shift+Tab stay inside the top modal;
 * when it closes, focus goes back to whatever had it before. One watcher for every sheet, modal and
 * confirmation, so screens only need `aria-modal="true"` (or `role="alertdialog"`).
 */

const MODAL = '[aria-modal="true"], [role="alertdialog"]';
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function topModal(doc: Document): HTMLElement | null {
  const all = [...doc.querySelectorAll<HTMLElement>(MODAL)];
  for (let i = all.length - 1; i >= 0; i--) {
    const el = all[i];
    if (el && !el.closest('[hidden], [aria-hidden="true"]')) return el;
  }
  return null;
}

function focusables(el: HTMLElement): HTMLElement[] {
  return [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (x) => !x.closest('[hidden], [aria-hidden="true"]'),
  );
}

function focusInto(el: HTMLElement): void {
  const target =
    el.querySelector<HTMLElement>('[data-autofocus]') ??
    el.querySelector<HTMLElement>('h1, h2, h3') ??
    focusables(el)[0] ??
    el;
  if (!target.matches(FOCUSABLE) && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
}

export function attachFocusTrap(doc: Document = document): () => void {
  let top: HTMLElement | null = null;
  const returnTo = new WeakMap<HTMLElement, HTMLElement>();

  const update = () => {
    const next = topModal(doc);
    if (next === top) return;
    const prev = top;
    top = next;
    if (next && !(prev && next.contains(prev))) {
      // A new layer on top: remember where focus was, then step inside.
      const active = doc.activeElement;
      if (active instanceof HTMLElement && !next.contains(active)) returnTo.set(next, active);
      if (!next.contains(doc.activeElement)) focusInto(next);
      return;
    }
    // The top layer went away: back to where focus was before it opened, if that is still there.
    const back = prev ? returnTo.get(prev) : undefined;
    if (back?.isConnected && (!next || next.contains(back))) back.focus({ preventScroll: true });
    else if (next && !next.contains(doc.activeElement)) focusInto(next);
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Tab' || !top) return;
    const list = focusables(top);
    const first = list[0];
    const last = list.at(-1);
    if (!first || !last) {
      e.preventDefault();
      return;
    }
    const active = doc.activeElement;
    if (!top.contains(active)) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && (active === first || active === top)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const observer = new MutationObserver(update);
  observer.observe(doc.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-modal', 'role', 'hidden', 'aria-hidden'],
  });
  doc.addEventListener('keydown', onKey, true);
  update();
  return () => {
    observer.disconnect();
    doc.removeEventListener('keydown', onKey, true);
  };
}
